import { Paper } from '@mui/material';
import Typography from '@mui/material/Typography';
import React from 'react';
import SVGInline from 'react-svg-inline';
import { useProgramRegistrationsQuery } from '../../hooks/usePrograms';
import { AllDone } from './AllDone';
import ProgramRegistrationModal from './modals/ProgramRegistrationModal';
import { getDaysDifference } from './utils';
import WidgetListItem from './WidgetListItem';

const checkCircleIcon = require('../../../images/check-circle.svg.raw');

const ProgramRegistrationsWidget = ({ institute }) => {
  const { data: programRegistrations, isPending } =
    useProgramRegistrationsQuery();


  const [programRegistrationModalOpen, setProgramRegistrationModalOpen] =
    React.useState(false);
  const [selectedProgramId, setSelectedProgramId] = React.useState(null);
  const [selectedRegistration, setSelectedRegistration] = React.useState(null);

  const programRegistrationsAsWidgetFormat = React.useMemo(() => {
    if (!programRegistrations || programRegistrations?.length === 0) return [];

    return programRegistrations.map((registration) => {
      const overdueDays = getDaysDifference(registration.created_at)
      
      return {
        id: registration.id,
        name: registration.user_name || 'Unknown User',
        createdAt: registration.created_at,
        overdueDays: overdueDays > 0 ? overdueDays : 0,
        ...registration
      };
    });
  }, [programRegistrations]);

  const overdueCount = React.useMemo(() => {
    const threshold = institute?.registration_warning_days;

    return programRegistrationsAsWidgetFormat.filter(
      (p) => threshold && (p.overdueDays || 0) >= threshold
    ).length;
  }, [institute, programRegistrationsAsWidgetFormat]);

  const totalCount = programRegistrations?.length;

  const onItemClick = (registration) => {
    setSelectedRegistration(registration);
    setSelectedProgramId(registration.program_id);
    setProgramRegistrationModalOpen(true);
  };

  const handleCloseProgramRegistrationModal = () => {
    setProgramRegistrationModalOpen(false);
    setSelectedProgramId(null);
  };

  return (
    <>
      <Paper
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            borderBottom: '1px solid #eee'
          }}
        >
          <Typography
            variant="h6"
            style={{ marginRight: 12, fontSize: 16, fontWeight: '600' }}
          >
            Program Registrations
          </Typography>
          {isPending ? (
            <div></div>
          ) : !totalCount ? (
            <div style={{ width: 20, height: 20, alignItems: 'center' }}>
              <SVGInline svg={checkCircleIcon} />
            </div>
          ) : (
            <span
              style={{
                background: '#F5F5F5',
                borderRadius: 8,
                padding: '4px 8px',
                fontWeight: 500,
                fontSize: 13,
                color: '#1F1F1F',
                marginLeft: 0,
                display: 'inline-block'
              }}
            >
              {overdueCount}/{totalCount}{totalCount === 25 ? '+' : ''}
            </span>
          )}
        </div>
        <div
          style={{ maxHeight: 360, overflowY: 'auto', padding: 0, margin: 0 }}
        >
          {isPending ? (
            <Typography style={{ marginLeft: 16 }}>Loading...</Typography>
          ) : !programRegistrations.length ? (
            <AllDone />
          ) : (
            programRegistrationsAsWidgetFormat.map(
              (programRegistration, idx) => (
                <WidgetListItem
                  key={programRegistration.id}
                  createdAt={programRegistration.createdAt}
                  name={programRegistration.name}
                  overdueDays={programRegistration.overdueDays}
                  onButtonClick={() => onItemClick(programRegistration)}
                  disableDivider={
                    idx === programRegistrationsAsWidgetFormat.length - 1
                  }
                  warningThreshold={institute?.registration_warning_days || 7}
                  overdueThreshold={institute?.registration_overdue_days || 14}
                />
              )
            )
          )}
        </div>
      </Paper>
      <ProgramRegistrationModal
        open={programRegistrationModalOpen}
        onClose={handleCloseProgramRegistrationModal}
        registration={selectedRegistration}
        programId={selectedProgramId}
        institute={institute}
      />
    </>
  );
};

export default ProgramRegistrationsWidget;
